import { ArrowPathIcon, EyeIcon, SwatchIcon } from '@heroicons/react/24/outline'
import { LoanSubmissionDocument } from 'pages/LoanSubmission/types'
import { useEffect, useState } from 'react'
import { analyzeDocument, extractDocumentBySections, getAnalyzeDocumentHistory, openS3Document } from 'services'
import { Modal, Tooltip } from 'stories/components'
import { PlainTable } from 'stories/components/PlainTable'
import { confirm, formatTime } from 'utils'

import { ExtractDocumentSectionsModal } from './ExtractDocumentSectionsModal'
import { DocumentSection } from './types'

interface HistoryItem {
  id: number
  title: string
  bucketName: string
  fileKey: string
  createdAt: string
  createdBy: string
  status: DocumentAiParsedStatus
}

export enum DocumentAiParsedStatus {
  FAILED = 'FAILED',
  // PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
}

export const ExtractDocumentHistoryModal = ({ onClose }: { onClose: Function }) => {
  const [history, setHistory] = useState<HistoryItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [activeItem, setActiveItem] = useState<HistoryItem | null>(null)
  const [sections, setSections] = useState<DocumentSection[] | null>(null)

  useEffect(() => {
    getAnalyzeDocumentHistory()
      .then((data: HistoryItem[]) => setHistory(data))
      .finally(() => setIsLoading(false))
  }, [])

  const tableHeader = ['No', 'Title', 'Status', 'Created By', 'Created At', 'Actions']

  const onReanalyze = async (item: HistoryItem) => {
    const result = await confirm('Are you sure want to reanalyze this document?')
    if (!result) return

    setIsLoading(true)
    await analyzeDocument({ key: item.fileKey, name: item.title }, true)
    setIsLoading(false)
  }

  const onViewSections = async (item: HistoryItem) => {
    setIsLoading(true)
    const res = await analyzeDocument({
      id: item.id,
      key: item.fileKey,
      name: item.title,
    })
    setIsLoading(false)
    if (res.success && res.sections && res.sections.length > 0) {
      setActiveItem(item)
      setSections(res.sections)
    }
  }

  const onCloseSectionsModal = () => {
    setActiveItem(null)
    setSections(null)
  }

  const onExtractAndSaveSections = async (sections: DocumentSection[]) => {
    if (!activeItem?.fileKey) return

    const result = await confirm('Are you sure want to extract and save sections to document storage?')
    if (!result) return

    setIsLoading(true)
    await extractDocumentBySections(activeItem.fileKey, sections, activeItem.id)
    setIsLoading(false)

    setActiveItem(null)
    setSections(null)
  }

  const tableData = history.map((item, index) => [
    index + 1,
    item.title,
    item.status,
    item.createdBy,
    formatTime(item.createdAt),
    <div className="flex gap-2">
      <Tooltip message="View File">
        <button className="btn-icon" onClick={() => openS3Document(item.fileKey, true)}>
          <EyeIcon className="w-4 h-4" />
        </button>
      </Tooltip>
      {item.status == DocumentAiParsedStatus.COMPLETED && (
        <>
          <Tooltip message="View Sections" placement="right">
            <button className="btn-icon" onClick={() => onViewSections(item)}>
              <SwatchIcon className="w-4 h-4" />
            </button>
          </Tooltip>
          <Tooltip message="Reanalyze Document" placement="right">
            <button className="btn-icon" onClick={() => onReanalyze(item)}>
              <ArrowPathIcon className="w-4 h-4" />
            </button>
          </Tooltip>
        </>
      )}
    </div>,
  ])

  const activeDocument: LoanSubmissionDocument | null = activeItem
    ? ({
        key: activeItem.fileKey,
        name: activeItem.title,
      } as LoanSubmissionDocument)
    : null

  return (
    <>
      <Modal
        title="Analyze & Extract Document Logs"
        titleCancel="Close"
        titleOkay=""
        loading={isLoading}
        isOpen
        onClose={() => onClose()}
      >
        <div className="min-w-[600px]">
          {isLoading ? (
            <div className="text-center py-4">Loading...</div>
          ) : history.length === 0 ? (
            <div className="text-center py-4 text-gray-500">No history found.</div>
          ) : (
            <PlainTable
              header={tableHeader}
              data={tableData}
              tdClass="px-4 py-3 whitespace-nowrap"
              thClass="text-xs"
              hasPagination
              itemCountPerPage={10}
            />
          )}
        </div>
      </Modal>

      {activeDocument && sections && (
        <ExtractDocumentSectionsModal
          document={activeDocument}
          sections={sections}
          onClose={onCloseSectionsModal}
          onSubmit={onExtractAndSaveSections}
        />
      )}
    </>
  )
}
